import { sortHand, type Card, type HandResult, type Rank, type Seat, type Team } from "@guandan/engine";
import type {
  MatchSummary, Snapshot, TableAction, TablePhase, WireCombo, WireEventBody,
} from "./index.js";

/**
 * What one seat believes about the table.
 *
 * A pure reducer over the event stream, shared by the browser and the
 * end-to-end harness. Sharing it is the point: the harness plays whole matches
 * against a real server and checks this model against the server's own
 * snapshot, so the reducer the browser runs is one already proven not to drift.
 *
 * Everything here is immutable — React re-renders on identity change, and a
 * model that mutated in place would render stale.
 */

export interface TableModel {
  /** Null for a spectator, who sees counts but never cards. */
  readonly seat: Seat | null;
  readonly seq: number;
  readonly phase: TablePhase;
  readonly handNumber: number;
  readonly level: Rank;
  readonly playingTeam: Team;
  readonly levels: readonly [Rank, Rank];
  /** Own cards, kept sorted for display. */
  readonly hand: readonly Card[];
  /** Cards left in every seat, including this one. */
  readonly counts: readonly number[];
  readonly out: readonly boolean[];
  /** Finishing order so far. */
  readonly finished: readonly Seat[];
  /** The play holding the trick, or null when the trick is open. */
  readonly current: { seat: Seat; combo: WireCombo } | null;
  /** Who has passed since the last play — drives the "pass" markers. */
  readonly passed: readonly boolean[];
  /** One-based trick number and public actions for the current hand. */
  readonly trickNumber: number;
  readonly trickActions: readonly TableAction[];
  /** Kept on screen until the next trick receives its first action. */
  readonly lastTrickActions: readonly TableAction[];
  /** Bounded chronological play/pass log for the current hand. */
  readonly activity: readonly TableAction[];
  readonly turn: Seat;
  readonly open: boolean;
  /** Absolute server time; render it against a clock offset, never a countdown. */
  readonly deadlineAt: number | null;
  readonly turnReadyAt: number;
  readonly turnToken: string;
  /** Tribute cards on show this hand, oldest first. */
  readonly tribute: readonly TributeEntry[];
  readonly tributeResisted: readonly Seat[];
  readonly lastResult: HandResult | null;
  readonly winner: Team | null;
  readonly summary: MatchSummary | null;
}

export interface TributeEntry {
  readonly kind: "paid" | "returned";
  readonly from: Seat;
  readonly to: Seat;
  /** Null when the card is private to two other players. */
  readonly card: Card | null;
}

const NO_PASSES: readonly boolean[] = [false, false, false, false];

export function emptyModel(seat: Seat | null): TableModel {
  return {
    seat,
    seq: 0,
    phase: "playing",
    handNumber: 0,
    level: 2,
    playingTeam: 0,
    levels: [2, 2],
    hand: [],
    counts: [0, 0, 0, 0],
    out: [false, false, false, false],
    finished: [],
    current: null,
    passed: NO_PASSES,
    trickNumber: 1,
    trickActions: [],
    lastTrickActions: [],
    activity: [],
    turn: 0,
    open: true,
    deadlineAt: null,
    turnReadyAt: 0,
    turnToken: "",
    tribute: [],
    tributeResisted: [],
    lastResult: null,
    winner: null,
    summary: null,
  };
}

export function asTablePhase(phase: string): TablePhase {
  return phase === "returnTribute" || phase === "handOver" || phase === "matchOver"
    ? phase
    : "playing";
}

/** Adopt the server's own view wholesale. Used for a cold start or a long gap. */
export function fromSnapshot(snapshot: Snapshot, seat: Seat | null): TableModel {
  const fallbackAction: TableAction[] = snapshot.current === null ? [] : [{
    seq: snapshot.seq,
    trick: snapshot.trickNumber ?? 1,
    seat: snapshot.current.seat,
    kind: "played",
    combo: snapshot.current.combo,
  }];
  return {
    ...emptyModel(seat),
    seq: snapshot.seq,
    phase: asTablePhase(snapshot.phase),
    handNumber: snapshot.handNumber,
    level: snapshot.level,
    playingTeam: snapshot.playingTeam,
    levels: [snapshot.levels[0], snapshot.levels[1]],
    hand: sortHand([...snapshot.hand], snapshot.level),
    counts: [...snapshot.counts],
    out: [...snapshot.out],
    finished: [...snapshot.finished],
    current: snapshot.current === null
      ? null
      : { seat: snapshot.current.seat, combo: snapshot.current.combo },
    trickNumber: snapshot.trickNumber ?? 1,
    trickActions: [...(snapshot.trickActions ?? fallbackAction)],
    lastTrickActions: [...(snapshot.lastTrickActions ?? [])],
    activity: [...(snapshot.activity ?? fallbackAction)],
    turn: snapshot.turn,
    open: snapshot.open,
    deadlineAt: snapshot.deadlineAt,
    turnReadyAt: snapshot.turnReadyAt,
    turnToken: snapshot.turnToken,
    summary: snapshot.summary ?? null,
    lastResult: snapshot.lastResult ?? null,
  };
}

const ACTIVITY_LIMIT = 120;

function recordAction(model: TableModel, action: TableAction): Pick<TableModel, "trickActions" | "activity"> {
  // Resume boundaries can deliver an already-applied event. Keep the visual
  // log deterministic even when the transport repeats one.
  if (model.activity.some((entry) => entry.seq === action.seq)) {
    return { trickActions: model.trickActions, activity: model.activity };
  }
  return {
    trickActions: [...model.trickActions, action],
    activity: [...model.activity, action].slice(-ACTIVITY_LIMIT),
  };
}

function withCount(counts: readonly number[], seat: Seat, delta: number): number[] {
  const next = [...counts];
  next[seat] = Math.max(0, (next[seat] ?? 0) + delta);
  return next;
}

function withoutCards(hand: readonly Card[], cards: readonly Card[]): Card[] {
  const next = [...hand];
  for (const card of cards) {
    const at = next.indexOf(card);
    if (at >= 0) next.splice(at, 1);
  }
  return next;
}

/**
 * Fold one event into the model.
 *
 * `seq` only ever moves forward, so a duplicate delivery is harmless — which
 * matters because a resume replays from wherever the client last got to, and
 * the boundary is inclusive on the server's side of the fence.
 */
export function applyEvent(model: TableModel, body: WireEventBody, seq: number): TableModel {
  const seat = model.seat;
  const next = { ...model, seq: Math.max(model.seq, seq) };

  switch (body.type) {
    case "handStarted":
      return {
        ...next,
        handNumber: body.handNumber,
        level: body.level,
        playingTeam: body.playingTeam,
        levels: [body.levels[0], body.levels[1]],
        phase: "playing",
        current: null,
        passed: NO_PASSES,
        trickNumber: 1,
        trickActions: [],
        lastTrickActions: [],
        activity: [],
        out: [false, false, false, false],
        finished: [],
        tribute: [],
        tributeResisted: [],
        lastResult: null,
      };

    case "dealt":
      return {
        ...next,
        hand: sortHand([...body.hand], next.level),
        counts: [...body.counts],
      };

    case "tributeResisted":
      return { ...next, tributeResisted: [...body.seats] };

    case "tributePaid":
    case "tributeReturned": {
      // Counts move for everyone; the card itself moves only for the two
      // players involved, and only when the projection let this seat see it.
      let counts = withCount(next.counts, body.from, -1);
      counts = withCount(counts, body.to, +1);
      let hand = next.hand;
      if (body.card !== null) {
        if (body.from === seat) hand = withoutCards(hand, [body.card]);
        if (body.to === seat) hand = sortHand([...hand, body.card], next.level);
      }
      return {
        ...next,
        counts,
        hand,
        tribute: [...next.tribute, {
          kind: body.type === "tributePaid" ? "paid" : "returned",
          from: body.from,
          to: body.to,
          card: body.card,
        }],
      };
    }

    case "turn":
      return {
        ...next,
        turn: body.seat,
        open: body.open,
        phase: body.phase,
        deadlineAt: body.deadlineAt,
        turnReadyAt: body.turnReadyAt,
        turnToken: body.turnToken,
      };

    case "played": {
      const counts = [...next.counts];
      counts[body.seat] = body.remaining;
      const action = recordAction(next, {
        seq, trick: next.trickNumber, seat: body.seat, kind: "played", combo: body.combo,
      });
      return {
        ...next,
        hand: body.seat === seat ? withoutCards(next.hand, body.cards) : next.hand,
        counts,
        current: { seat: body.seat, combo: body.combo },
        ...action,
        // The prior trick leaves the felt when this one actually begins.
        lastTrickActions: next.trickActions.length === 0 ? [] : next.lastTrickActions,
        // A play reopens the question for everyone else.
        passed: NO_PASSES,
      };
    }

    case "passed": {
      const passed = [...next.passed];
      passed[body.seat] = true;
      const action = recordAction(next, {
        seq, trick: next.trickNumber, seat: body.seat, kind: "passed", combo: null,
      });
      return { ...next, passed, ...action };
    }

    case "trickEnded":
      return {
        ...next,
        current: null,
        passed: NO_PASSES,
        trickNumber: next.trickNumber + 1,
        lastTrickActions: next.trickActions,
        trickActions: [],
      };

    case "playerOut": {
      const out = [...next.out];
      out[body.seat] = true;
      return {
        ...next,
        out,
        finished: next.finished.includes(body.seat)
          ? next.finished
          : [...next.finished, body.seat],
      };
    }

    case "handEnded":
      // Nobody is on turn between hands. Leaving `turn` pointing at whoever
      // acted last is how a client ends up trying to play into `handOver`.
      return { ...next, phase: "handOver", lastResult: body.result, current: null, passed: NO_PASSES };

    case "matchEnded":
      return { ...next, phase: "matchOver", winner: body.winner, summary: body.summary ?? next.summary };

    default:
      return next;
  }
}

/** Does this seat owe an action right now? */
export function isOnTurn(model: TableModel): boolean {
  return (
    model.seat !== null &&
    model.turn === model.seat &&
    (model.phase === "playing" || model.phase === "returnTribute")
  );
}

export const partnerSeat = (seat: Seat): Seat => ((seat + 2) % 4) as Seat;

/**
 * Where a seat sits relative to the viewer: self at the bottom, partner across,
 * opponents left and right. Spectators view from seat 0.
 */
export function relativeSeat(seat: Seat, viewer: Seat | null): 0 | 1 | 2 | 3 {
  return (((seat - (viewer ?? 0)) % 4) + 4) % 4 as 0 | 1 | 2 | 3;
}
